<template>
  <div class="onboard-tenant">
    <!-- Header -->
    <div class="ob-header">
      <router-link
        :to="{ name: 'accounts' }"
        class="ob-back"
      >
        <Icon type="ios-arrow-back" />
        {{ $t("Back to accounts") }}
      </router-link>
      <h2 class="ob-title">
        <Icon
          type="md-briefcase"
          class="ob-title__icon"
        />
        {{ $t("Onboard a new client") }}
      </h2>
      <p class="ob-subtitle">
        {{ $t("Create a client, its contract and invite its first manager — in a few steps.") }}
      </p>
    </div>

    <!-- Stepper -->
    <div class="ob-steps-card">
      <Steps :current="step">
        <Step
          :title="$t('Client')"
          :content="$t('Company details')"
        />
        <Step
          :title="$t('Contract')"
          :content="$t('Dates & domains')"
        />
        <Step
          :title="$t('First manager')"
          :content="$t('Invite an admin')"
        />
        <Step
          :title="$t('Done')"
          :content="$t('Ready to use')"
        />
      </Steps>
    </div>

    <!-- Step 1 — Client -->
    <Card
      v-if="step === 0"
      class="ob-card"
      dis-hover
    >
      <div class="ob-section-head">
        <Icon
          type="md-briefcase"
          class="ob-section-head__icon"
        />
        <div>
          <h3 class="ob-section-title">
            {{ $t("Client information") }}
          </h3>
          <p class="ob-section-desc">
            {{ $t("These details identify the client organization and its billing entity.") }}
          </p>
        </div>
      </div>
      <Form
        ref="clientForm"
        :model="client"
        :rules="clientRules"
        :label-width="160"
        label-position="left"
        class="ob-form ob-form--grid"
      >
        <FormItem
          :label="$t('Client name')"
          prop="client_name"
        >
          <Input
            v-model="client.client_name"
            :placeholder="$t('e.g. SNEF')"
          />
        </FormItem>
        <FormItem :label="$t('Type')">
          <Select v-model="client.client_type">
            <Option value="professionnal">
              {{ $t("Professional") }}
            </Option>
            <Option value="individual">
              {{ $t("Individual") }}
            </Option>
          </Select>
        </FormItem>
        <FormItem
          :label="$t('Contact name')"
          prop="client_contact_name"
        >
          <Input v-model="client.client_contact_name" />
        </FormItem>
        <FormItem
          :label="$t('Contact email')"
          prop="client_email"
        >
          <Input v-model="client.client_email" />
        </FormItem>
        <FormItem
          :label="$t('Phone')"
          prop="client_phone"
          :error="serverErrors.client_phone"
        >
          <Input
            v-model="client.client_phone"
            :placeholder="$t('e.g. +33123456789')"
            @on-focus="clearServerError('client_phone')"
          />
        </FormItem>
        <FormItem
          :label="$t('Billing country')"
          prop="client_billing_country"
        >
          <Select
            v-model="client.client_billing_country"
            filterable
          >
            <Option
              v-for="c in worldInfoStore.countryList"
              :key="c.iso"
              :value="c.iso"
            >
              {{ c.display_name }}
            </Option>
          </Select>
        </FormItem>
        <FormItem
          :label="$t('Billing city')"
          prop="client_billing_city"
        >
          <Input v-model="client.client_billing_city" />
        </FormItem>
        <FormItem
          :label="$t('Billing address')"
          prop="client_billing_address"
        >
          <Input v-model="client.client_billing_address" />
        </FormItem>
      </Form>
      <div class="ob-actions">
        <Button
          type="primary"
          :loading="loading"
          @click="createClient"
        >
          {{ $t("Create client & continue") }}
        </Button>
      </div>
    </Card>

    <!-- Step 2 — Contract -->
    <Card
      v-if="step === 1"
      class="ob-card"
      dis-hover
    >
      <div class="ob-section-head">
        <Icon
          type="md-document"
          class="ob-section-head__icon"
        />
        <div>
          <h3 class="ob-section-title">
            {{ $t("Contract") }}
          </h3>
          <p class="ob-section-desc">
            {{ $t("Set the contract period, user limit and the email domains allowed for this client.") }}
          </p>
        </div>
      </div>
      <Form
        :label-width="160"
        label-position="left"
        class="ob-form ob-form--grid"
      >
        <FormItem :label="$t('Contract name')">
          <Input v-model="contract.contract_name" />
        </FormItem>
        <FormItem :label="$t('User limit')">
          <Select v-model="contract.contract_user_limit">
            <Option :value="20">
              20
            </Option>
            <Option :value="50">
              50
            </Option>
            <Option :value="100">
              100
            </Option>
            <Option :value="250">
              250
            </Option>
            <Option :value="999999">
              {{ $t("Unlimited") }}
            </Option>
          </Select>
        </FormItem>
        <FormItem :label="$t('Start date')">
          <DatePicker
            v-model="contract.contract_start_date"
            type="date"
            style="width:100%"
          />
        </FormItem>
        <FormItem :label="$t('End date')">
          <DatePicker
            v-model="contract.contract_end_date"
            type="date"
            style="width:100%"
          />
        </FormItem>
        <FormItem
          :label="$t('Authorised domains')"
          class="ob-form__full"
        >
          <Input
            v-model="domainsRaw"
            :placeholder="$t('comma-separated, e.g. snef.fr, contractor.com')"
          />
        </FormItem>
      </Form>
      <div class="ob-actions ob-actions--split">
        <Button
          icon="ios-arrow-back"
          @click="step = 0"
        >
          {{ $t("Back") }}
        </Button>
        <Button
          type="primary"
          :loading="loading"
          @click="createContract"
        >
          {{ $t("Create contract & continue") }}
        </Button>
      </div>
    </Card>

    <!-- Step 3 — First manager -->
    <Card
      v-if="step === 2"
      class="ob-card"
      dis-hover
    >
      <div class="ob-section-head">
        <Icon
          type="md-person-add"
          class="ob-section-head__icon"
        />
        <div>
          <h3 class="ob-section-title">
            {{ $t("First manager") }}
          </h3>
          <p class="ob-section-desc">
            {{ $t("Invite the first manager of this client. They'll receive an email to set their password.") }}
          </p>
        </div>
      </div>
      <Form
        :label-width="160"
        label-position="left"
        class="ob-form"
      >
        <FormItem :label="$t('Manager email')">
          <Input
            v-model="managerEmail"
            prefix="ios-mail-outline"
            :placeholder="$t('must match an authorised domain')"
          />
        </FormItem>
      </Form>
      <div class="ob-actions ob-actions--split">
        <Button
          icon="ios-arrow-back"
          @click="step = 1"
        >
          {{ $t("Back") }}
        </Button>
        <div class="ob-actions__right">
          <Button @click="finishWithoutManager">
            {{ $t("Skip") }}
          </Button>
          <Button
            type="primary"
            :loading="loading"
            @click="inviteManager"
          >
            {{ $t("Invite manager & finish") }}
          </Button>
        </div>
      </div>
    </Card>

    <!-- Step 4 — Done -->
    <Card
      v-if="step === 3"
      class="ob-card ob-done"
      dis-hover
    >
      <div class="ob-done__badge">
        <Icon type="md-checkmark" />
      </div>
      <h3 class="ob-done__title">
        {{ $t("Client onboarded!") }}
      </h3>
      <p class="ob-done__text">
        <strong>{{ createdClientName }}</strong> {{ $t("is ready.") }}
      </p>
      <p class="ob-done__hint">
        {{ $t("Its organization tree has already been created. You can start adding entities and users.") }}
      </p>
      <div class="ob-actions ob-done__actions">
        <router-link :to="{ name: 'organization' }">
          <Button
            type="primary"
            icon="md-git-network"
          >
            {{ $t("Open organization tree") }}
          </Button>
        </router-link>
        <router-link :to="{ name: 'accounts' }">
          <Button>{{ $t("Back to accounts") }}</Button>
        </router-link>
      </div>
    </Card>
  </div>
</template>

<script>
import { postClientQry, onboardManagerQry } from "@/services/accounts.service";
import { postClientContractQry } from "@/services/contract.service";

export default {
  name: "OnboardTenant",
  data() {
    return {
      step: 0,
      loading: false,
      createdClientId: null,
      createdClientName: "",
      createdContractId: null,
      client: {
        client_name: "",
        client_type: "professionnal",
        client_contact_name: "",
        client_email: "",
        client_phone: "",
        client_billing_country: "FR",
        client_billing_city: "",
        client_billing_address: "",
      },
      contract: {
        contract_name: "",
        contract_start_date: null,
        contract_end_date: null,
        contract_user_limit: 50,
      },
      domainsRaw: "",
      managerEmail: "",
      // Field-level errors returned by the backend (shown under each input).
      serverErrors: {},
    };
  },
  computed: {
    // The backend Client model requires these fields (billing entity); validate
    // them on the client side so the user gets clear, per-field feedback BEFORE
    // the request instead of 5 generic "Ce champ ne peut être vide" after it.
    clientRules() {
      const required = (msg) => ({
        required: true,
        message: msg,
        trigger: "blur",
      });
      return {
        client_name: [required(this.$t("Client name is required"))],
        client_contact_name: [required(this.$t("Contact name is required"))],
        client_email: [
          required(this.$t("Contact email is required")),
          {
            type: "email",
            message: this.$t("Please enter a valid email"),
            trigger: "blur",
          },
        ],
        client_phone: [
          required(this.$t("Phone is required")),
          {
            // Backend expects an international number (e.g. +33...). Hint early.
            pattern: /^\+?[0-9\s().-]{6,20}$/,
            message: this.$t("Enter a valid phone number, ideally in international format (e.g. +33612345678)."),
            trigger: "blur",
          },
        ],
        client_billing_country: [required(this.$t("Billing country is required"))],
        client_billing_city: [required(this.$t("Billing city is required"))],
        client_billing_address: [required(this.$t("Billing address is required"))],
      };
    },
  },
  methods: {
    fail(err, fallback) {
      const data = err && err.response && err.response.data;
      let msg = fallback;
      if (data) {
        if (data.detail) msg = Array.isArray(data.detail) ? data.detail[0] : data.detail;
        else if (typeof data === "object") msg = Object.values(data).flat().join(" ");
      }
      this.$Message.error(msg);
    },
    async createClient() {
      // Validate all required client fields up-front (per-field messages in the form).
      const valid = await new Promise((resolve) =>
        this.$refs.clientForm.validate((ok) => resolve(ok))
      );
      if (!valid) {
        this.$Message.warning(this.$t("Please fill in the required fields"));
        return;
      }
      this.serverErrors = {};
      this.loading = true;
      try {
        const res = await postClientQry(this.client);
        this.createdClientId = res.data.id;
        this.createdClientName = res.data.client_name;
        this.$Message.success(this.$t("Client created"));
        this.step = 1;
      } catch (e) {
        // Show per-field backend errors under the inputs; toast only the rest.
        const handled = this.applyServerErrors(e);
        if (!handled) this.fail(e, this.$t("Could not create client"));
      } finally {
        this.loading = false;
      }
    },
    // Map DRF field errors ({field: ["msg"]}) onto serverErrors so they render
    // under the matching input. Returns true if at least one field error was mapped.
    applyServerErrors(err) {
      const data = err && err.response && err.response.data;
      if (!data || typeof data !== "object") return false;
      const errors = {};
      let mapped = false;
      Object.keys(data).forEach((key) => {
        if (key in this.client) {
          const val = data[key];
          errors[key] = Array.isArray(val) ? val.join(" ") : String(val);
          mapped = true;
        }
      });
      // Friendlier message for the common phone-format rejection.
      if (errors.client_phone) {
        errors.client_phone = this.$t("Enter a valid phone number, ideally in international format (e.g. +33612345678).");
      }
      this.serverErrors = errors;
      if (mapped) this.$Message.error(this.$t("Please fix the highlighted fields."));
      return mapped;
    },
    clearServerError(field) {
      if (this.serverErrors[field]) {
        this.$set(this.serverErrors, field, "");
      }
    },
    // iView's DatePicker binds a JS Date (or null). The backend fields are
    // DateTimeField, so they need a full ISO datetime (YYYY-MM-DDThh:mm:ss...).
    // Convert here; return null for an empty/invalid value.
    toIsoDateTime(value) {
      if (!value) return null;
      const d = value instanceof Date ? value : new Date(value);
      return isNaN(d.getTime()) ? null : d.toISOString();
    },
    async createContract() {
      const startIso = this.toIsoDateTime(this.contract.contract_start_date);
      const endIso = this.toIsoDateTime(this.contract.contract_end_date);
      if (!this.contract.contract_name) {
        this.$Message.warning(this.$t("Contract name is required"));
        return;
      }
      if (!startIso || !endIso) {
        this.$Message.warning(this.$t("Start and end dates are required"));
        return;
      }
      if (new Date(startIso) >= new Date(endIso)) {
        this.$Message.warning(this.$t("End date must be after the start date."));
        return;
      }
      this.loading = true;
      try {
        const payload = {
          contract_name: this.contract.contract_name,
          contract_start_date: startIso,
          contract_end_date: endIso,
          contract_user_limit: this.contract.contract_user_limit,
          contract_is_active: true,
          domains_or_emails: this.domainsRaw
            .split(",")
            .map((d) => d.trim())
            .filter(Boolean),
        };
        const res = await postClientContractQry(this.createdClientId, payload);
        this.createdContractId = res.data.id;
        this.$Message.success(this.$t("Contract created"));
        this.step = 2;
      } catch (e) {
        this.fail(e, this.$t("Could not create contract"));
      } finally {
        this.loading = false;
      }
    },
    async inviteManager() {
      if (!this.managerEmail) {
        this.$Message.warning(this.$t("Manager email is required"));
        return;
      }
      this.loading = true;
      try {
        await onboardManagerQry(this.createdClientId, {
          email: this.managerEmail,
          contract: this.createdContractId,
        });
        this.$Message.success(this.$t("Manager invited"));
        this.step = 3;
      } catch (e) {
        this.fail(e, this.$t("Could not invite manager"));
      } finally {
        this.loading = false;
      }
    },
    finishWithoutManager() {
      this.step = 3;
    },
  },
};
</script>

<style scoped>
.onboard-tenant {
  padding: 24px;
  max-width: 1040px;
  margin: 0 auto;
  background: #f5f7fa;
  min-height: 100%;
}

/* ---- Header ---- */
.ob-header {
  margin-bottom: 20px;
}
.ob-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #808695;
  font-size: 13px;
  margin-bottom: 12px;
  transition: color 0.15s ease;
}
.ob-back:hover {
  color: #2d8cf0;
}
.ob-title {
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 600;
  color: #1f2d3d;
  margin: 0;
}
.ob-title__icon {
  font-size: 24px;
  color: #2d8cf0;
  margin-right: 10px;
}
.ob-subtitle {
  margin: 6px 0 0 34px;
  color: #808695;
  font-size: 13px;
}

/* ---- Stepper card ---- */
.ob-steps-card {
  background: #fff;
  border-radius: 10px;
  padding: 22px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  margin-bottom: 20px;
}

/* ---- Step cards ---- */
.ob-card {
  border-radius: 10px;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
}
.ob-card >>> .ivu-card-body {
  padding: 24px 28px;
}

/* ---- Section head ---- */
.ob-section-head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 22px;
  padding-bottom: 18px;
  border-bottom: 1px solid #f0f2f5;
}
.ob-section-head__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #e8f4ff;
  color: #2d8cf0;
  font-size: 22px;
  flex: 0 0 auto;
}
.ob-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2d3d;
  margin: 2px 0 2px;
}
.ob-section-desc {
  color: #808695;
  font-size: 13px;
  margin: 0;
}

/* ---- Forms (label left, input right, same row) ---- */
.ob-form >>> .ivu-form-item {
  margin-bottom: 18px;
}
.ob-form >>> .ivu-form-item-label {
  font-weight: 500;
  color: #515a6e;
  text-align: right;
  white-space: normal;
  line-height: 1.3;
}
.ob-form >>> .ivu-input,
.ob-form >>> .ivu-select-selection {
  border-radius: 6px;
}
.ob-form--grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 40px;
}
.ob-form__full {
  grid-column: 1 / -1;
}

@media (max-width: 900px) {
  .ob-form--grid {
    grid-template-columns: 1fr;
    gap: 0;
  }
}

/* ---- Actions ---- */
.ob-actions {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid #f0f2f5;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.ob-actions--split {
  justify-content: space-between;
}
.ob-actions__right {
  display: flex;
  gap: 10px;
}

/* ---- Done screen ---- */
.ob-done {
  text-align: center;
}
.ob-done__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e8fbef;
  color: #19be6b;
  font-size: 40px;
  margin: 10px auto 16px;
}
.ob-done__title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2d3d;
  margin: 0 0 6px;
}
.ob-done__text {
  color: #515a6e;
  font-size: 15px;
  margin: 0 0 4px;
}
.ob-done__hint {
  color: #808695;
  font-size: 13px;
  margin: 0 auto 8px;
  max-width: 420px;
}
.ob-done__actions {
  justify-content: center;
  border-top: none;
}
</style>
